import React from 'react';
import Skeleton from '@mui/material/Skeleton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhotoFilmMusic } from '@fortawesome/pro-solid-svg-icons/faPhotoFilmMusic';
import { renderToString } from 'react-dom/server';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { uuid } from '@loyalty/shared-common';
import { GenericComponent } from '../../components-tree';
import { ReactBootstrap } from '../../ReactBootstrap';
import { Widget } from '../Widget';
import { WidgetProps } from '../render-component';
import { extractOembed } from './extract-oembed';

export interface EmbeddedMediaComponentAttributes {
  targetUrl: string;
  spotifySize?: string;
}

export type EmbeddedMediaComponent = GenericComponent<
  EmbeddedMediaComponentAttributes,
  EmbeddedMediaComponentAttributes
>;

export type EmbeddedMediaWidgetBackendProps = WidgetProps<EmbeddedMediaComponent>;

interface EmbeddedMediaProps {
  id: string;
  attributes: EmbeddedMediaComponent['attributes'];
  i18nAttributes: EmbeddedMediaComponent['i18nAttributes'];
}

function Message({ children, visible = true }: { children: string; visible?: boolean }) {
  return (
    <div className={`qlfeditor-embedded-media-error ${visible ? 'visible' : ''}`}>
      <FontAwesomeIcon style={{ width: '35px', height: '35px' }} icon={faPhotoFilmMusic} />
      <div>{children}</div>
    </div>
  );
}

function Component({ id, attributes, i18nAttributes }: EmbeddedMediaProps) {
  const { t, i18n } = useTranslation();

  const targetUrl = i18nAttributes?.[i18n.language]?.targetUrl || attributes.targetUrl;
  const spotifySize = i18nAttributes?.[i18n.language]?.spotifySize || attributes.spotifySize;

  const oembed = useQuery({
    queryKey: ['oembed', targetUrl],
    queryFn: () => extractOembed(targetUrl),
    enabled: !!targetUrl,
  });

  return (
    <section
      id={id}
      className="embedded-media-component"
      aria-label={t('aria.embeddedMedia.label')}
      aria-description={t('aria.embeddedMedia.description', {
        provider: oembed.data?.provider_name || '',
      })}
      style={{ flex: 1 }}
    >
      {!targetUrl ? (
        <Message>{t('editor.embedded-media.empty-state.add')}</Message>
      ) : oembed.isPending ? (
        <Message>{t('editor.embedded-media.empty-state.loading')}</Message>
      ) : oembed.isError ? (
        <Message>{t('editor.embedded-media.provider.error')}</Message>
      ) : !oembed.data?.html ? (
        <Message>{t('editor.oembed.errors.no-html')}</Message>
      ) : oembed.data.provider_name === 'YouTube' ? (
        <div className="youtube-container">
          <div className="youtube-iframe" dangerouslySetInnerHTML={{ __html: oembed.data.html }} />
        </div>
      ) : oembed.data.provider_name === 'Spotify' ? (
        <div
          className={`spotify-container-${spotifySize}`}
          dangerouslySetInnerHTML={{ __html: oembed.data.html }}
        />
      ) : (
        <div className="qlfeditor-embedded-media-error visible">
          <div>{t('editor.oembed.errors.no-html')}</div>
          <div>{t('editor.embedded-media.empty-state.change')}</div>
        </div>
      )}
      <Message visible={false}>{t('editor.embedded-media.empty-state.change')}</Message>
    </section>
  );
}

export function EmbeddedMediaComponent(props: EmbeddedMediaProps) {
  return (
    <Widget loader={<Skeleton />}>
      <Component {...props} />
    </Widget>
  );
}

export function EmbeddedMediaRenderer(
  props: EmbeddedMediaWidgetBackendProps,
  document?: Document,
): string {
  return renderToString(
    <ReactBootstrap
      componentType="embedded-media"
      component="EmbeddedMediaComponent"
      props={{
        id: props.options.attributes.id || `_${uuid()}`,
        attributes: props.options.attributes,
        i18nAttributes: props.options.i18nAttributes,
      }}
      document={document}
    />,
  );
}
